/**
 * Common validation rules for encoder tools
 */

import { EncoderToolType } from '../types';

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  allowedTypes?: string[];
  maxSize?: number;
  custom?: (value: any) => boolean | string;
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  suggestion?: string;
}

export class CommonValidator {
  /**
   * Validate text input
   */
  static validateText(text: string, rules: ValidationRule): ValidationResult {
    const { required, minLength, maxLength, pattern, custom } = rules;

    // Required validation
    if (required && !text.trim()) {
      return {
        isValid: false,
        error: 'Input is required',
        suggestion: 'Please enter some text'
      };
    }

    // Skip other validations if empty and not required
    if (!text.trim() && !required) {
      return { isValid: true };
    }

    // Length validation
    if (minLength && text.length < minLength) {
      return {
        isValid: false,
        error: `Input must be at least ${minLength} characters`,
        suggestion: `Add ${minLength - text.length} more characters`
      };
    }

    if (maxLength && text.length > maxLength) {
      return {
        isValid: false,
        error: `Input must not exceed ${maxLength} characters`,
        suggestion: `Remove ${text.length - maxLength} characters`
      };
    }

    // Pattern validation
    if (pattern && !pattern.test(text)) {
      return {
        isValid: false,
        error: 'Input format is invalid',
        suggestion: 'Please check the format and try again'
      };
    }

    // Custom validation
    if (custom) {
      const customResult = custom(text);
      if (typeof customResult === 'string') {
        return {
          isValid: false,
          error: customResult,
          suggestion: 'Please check your input and try again'
        };
      }
      if (!customResult) {
        return {
          isValid: false,
          error: 'Input validation failed',
          suggestion: 'Please check your input and try again'
        };
      }
    }

    return { isValid: true };
  }

  /**
   * Validate file upload
   */
  static validateFile(file: File, rules: ValidationRule): ValidationResult {
    const { allowedTypes, maxSize, custom } = rules;

    // File type validation
    if (allowedTypes && !allowedTypes.includes(file.type)) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension || !allowedTypes.some(type => type.includes(extension))) {
        return {
          isValid: false,
          error: 'File type not supported',
          suggestion: `Please use one of the following types: ${allowedTypes.join(', ')}`
        };
      }
    }

    // File size validation
    if (maxSize && file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      return {
        isValid: false,
        error: `File size exceeds ${maxSizeMB.toFixed(1)}MB limit`,
        suggestion: 'Use a smaller file or compress it first'
      };
    }

    // Custom validation
    if (custom) {
      const customResult = custom(file);
      if (typeof customResult === 'string') {
        return {
          isValid: false,
          error: customResult,
          suggestion: 'Please check your file and try again'
        };
      }
      if (!customResult) {
        return {
          isValid: false,
          error: 'File validation failed',
          suggestion: 'Please check your file and try again'
        };
      }
    }

    return { isValid: true };
  }

  /**
   * Get validation rules for specific tool types
   */
  static getValidationRules(toolType: EncoderToolType): ValidationRule {
    switch (toolType) {
      case EncoderToolType.BASE64_TEXT:
        return {
          required: true,
          maxLength: 1000000, // 1MB text limit
          custom: (text: string) => {
            if (text.length > 10000000) { // 10MB absolute limit
              return 'Input is too large for processing';
            }
            return true;
          }
        };

      case EncoderToolType.BASE64_IMAGE:
        return {
          required: true,
          allowedTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'],
          maxSize: 10 * 1024 * 1024 // 10MB
        };

      case EncoderToolType.QR_CODE:
        return {
          required: true,
          maxLength: 4296, // QR code max data capacity
          custom: (text: string) => {
            // Check if text is reasonable for QR code
            if (text.length > 2000) {
              return 'Text is too long for QR code generation. Try shorter text or use a higher error correction level.';
            }
            return true;
          }
        };

      case EncoderToolType.URL_ENCODER:
        return {
          required: true,
          maxLength: 2048, // Standard URL length limit
          pattern: /^https?:\/\/.+/,
          custom: (text: string) => {
            try {
              new URL(text);
              return true;
            } catch {
              return 'Please enter a valid URL including protocol (http:// or https://)';
            }
          }
        };

      case EncoderToolType.JWT_TOKEN:
        return {
          required: true,
          pattern: /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/,
          custom: (token: string) => {
            const parts = token.split('.');
            if (parts.length !== 3) {
              return 'JWT must have exactly 3 parts separated by dots';
            }
            try {
              // Try to decode each part
              parts.forEach(part => atob(part));
              return true;
            } catch {
              return 'JWT contains invalid Base64 encoding';
            }
          }
        };

      case EncoderToolType.HTML_ENTITY:
        return {
          required: true,
          maxLength: 50000, // 50KB HTML limit
          custom: (text: string) => {
            // Basic HTML validation
            if (/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi.test(text)) {
              return 'HTML with script tags is not supported for security reasons';
            }
            return true;
          }
        };

      case EncoderToolType.GZIP_COMPRESSION:
        return {
          required: true,
          maxLength: 1000000, // 1MB text limit
          custom: (text: string) => {
            if (text.length === 0) {
              return 'Text cannot be empty for compression';
            }
            return true;
          }
        };

      case EncoderToolType.CERTIFICATE_DECODER:
        return {
          required: true,
          allowedTypes: ['application/x-x509-ca-cert', 'application/x-pem-file', 'text/plain'],
          maxSize: 1024 * 1024, // 1MB certificate limit
          custom: (file: File) => {
            const extension = file.name.split('.').pop()?.toLowerCase();
            const allowedExtensions = ['crt', 'cer', 'pem', 'der'];
            if (!extension || !allowedExtensions.includes(extension)) {
              return 'Certificate file must have one of these extensions: .crt, .cer, .pem, .der';
            }
            return true;
          }
        };

      case 'text-escape' as any:
        return {
          required: true,
          maxLength: 1000000, // 1MB text limit
          custom: (text: string) => {
            if (text.length === 0) {
              return 'Text cannot be empty';
            }
            return true;
          }
        };

      case 'list-compare' as any:
        return {
          required: false,
          maxLength: 100000, // 100KB text limit
          custom: (_text: string) => {
            return true;
          }
        };

      case 'markdown-preview' as any:
        return {
          required: false,
          maxLength: 100000, // 100KB markdown limit
          custom: (_text: string) => {
            return true;
          }
        };

      case 'text-analyzer' as any:
        return {
          required: false,
          maxLength: 1000000, // 1MB text limit
          custom: (_text: string) => {
            return true;
          }
        };

      case 'text-compare' as any:
        return {
          required: false,
          maxLength: 100000, // 100KB text limit
          custom: (_text: string) => {
            return true;
          }
        };

      default:
        return { required: true };
    }
  }
}